import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { daysUntil, formatDate } from '@/lib/dates';

/**
 * לומדות וחידונים של העובד (PRD 5.6).
 *
 * הרשימה מגיעה מ RPC אחת (my_learning) ולא משאילתה על הטבלאות, כי
 * השיוך הוא לפי כללים: סניף, תפקיד, עובד מסוים, או "רק מי שהתחיל
 * בחודש האחרון". הלוגיקה הזו יושבת בשרת, ולא משוכפלת כאן.
 */

export type AssignedCourse = {
  assignment_id: string;
  course_id: string;
  title: string;
  description: string | null;
  cover_path: string | null;
  category: string | null;
  due_date: string | null;
  is_mandatory: boolean;
  lesson_count: number;
  completed_count: number;
  quiz_id: string | null;
  quiz_passed: boolean;
};

/**
 * לומדה גמורה רק כשכל השיעורים סומנו, ואם יש לה חידון גם הוא עבר.
 * בלי זה עובד שנכשל בחידון היה רואה "הושלמה" ובדוח של ההנהלה היה
 * מופיע כמי שלא סיים.
 */
export function isCourseDone(course: AssignedCourse): boolean {
  if (course.lesson_count === 0 || course.completed_count < course.lesson_count) return false;
  return course.quiz_id === null || course.quiz_passed;
}

export type AssignedQuiz = {
  assignment_id: string;
  quiz_id: string;
  title: string;
  due_date: string | null;
  is_mandatory: boolean;
  pass_score: number;
  best_score: number | null;
  passed: boolean;
};

export type MyLearning = { courses: AssignedCourse[]; quizzes: AssignedQuiz[] };

export type Lesson = {
  id: string;
  title: string;
  sort_order: number;
  content_type: string;
  body: string | null;
  media_path: string | null;
  external_url: string | null;
  completed: boolean;
};

export type CourseDetail = {
  /** false כשמנהל/ת לומדות פותח/ת לומדה שלא הוטלה עליו/ה, לתצוגה בלבד */
  is_assigned: boolean;
  course: {
    id: string;
    title: string;
    description: string | null;
    cover_path: string | null;
    points_reward: number | null;
    quiz_id: string | null;
    quiz_passed: boolean;
  } | null;
  lessons: Lesson[];
};

export const LEARNING_KEY = ['learning'] as const;

export function useMyLearning() {
  return useQuery({
    queryKey: LEARNING_KEY,
    queryFn: async (): Promise<MyLearning> => {
      const { data, error } = await supabase.rpc('my_learning');
      if (error) throw error;
      // עובד בלי שיוכים מקבל null, לא אובייקט ריק
      return (data as MyLearning | null) ?? { courses: [], quizzes: [] };
    },
  });
}

export function useCourse(courseId: string) {
  return useQuery({
    queryKey: [...LEARNING_KEY, 'course', courseId],
    enabled: Boolean(courseId),
    queryFn: async (): Promise<CourseDetail | null> => {
      const { data, error } = await supabase.rpc('course_detail', { p_course: courseId });
      if (error) throw error;
      return data as CourseDetail | null;
    },
  });
}

export function useCompleteLesson(courseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (lessonId: string) => {
      const { error } = await supabase.rpc('complete_lesson', { p_lesson: lessonId });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...LEARNING_KEY, 'course', courseId] });
      void queryClient.invalidateQueries({ queryKey: LEARNING_KEY });
      // השלמת לומדה מזכה בנקודות, והמסך שלי מראה אותן
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/**
 * הצעד הבא בלומדה: השיעור הראשון שלא הושלם, או null כשסיימו הכל.
 * ככה "ממשיכים מאיפה שעצרו" (PRD 5.6.3) בלי לשמור מצב בצד הלקוח.
 */
export function nextLesson(lessons: Lesson[]): Lesson | null {
  return lessons.find((lesson) => !lesson.completed) ?? null;
}

/** כמה נשאר, בטקסט שאדם קורא. */
export function progressLabel(done: number, total: number): string {
  if (total === 0) return 'אין עוד שיעורים';
  if (done >= total) return 'סיימת את כל השיעורים';
  return `${done} מתוך ${total} שיעורים`;
}

/**
 * דחיפות לפי הדדליין. הצבע לבד אסור (כלל 10), ולכן כל מצב מחזיר גם
 * מילה.
 */
export function dueState(dueDate: string | null): {
  tone: 'warn' | 'bad' | 'calm';
  label: string;
} | null {
  if (!dueDate) return null;
  const days = daysUntil(dueDate);

  if (days < 0) {
    return { tone: 'bad', label: days === -1 ? 'באיחור של יום' : `באיחור של ${-days} ימים` };
  }
  if (days === 0) return { tone: 'bad', label: 'להשלמה היום' };
  if (days === 1) return { tone: 'warn', label: 'להשלמה עד מחר' };
  if (days <= 7) return { tone: 'warn', label: `נשארו ${days} ימים` };
  return { tone: 'calm', label: `להשלמה עד ${formatDate(dueDate)}` };
}
